import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Search, Trash2, X } from "lucide-react";
import {
  api,
  getDisplayProofTypes,
  getDisplayProofCompetitors,
  getProductGroups,
} from "../../services/api";
import { CurrencyContext } from "../../context/CurrencyContext";
import PriceRangeInput from "./PriceRangeInput";
import ProductGroupSelect from "../Common/ProductGroupSelect";
import ColourMeta from "../Common/ColourMeta";

const MAX_ITEMS = 5;
const MAX_FILES_PER_ITEM = 8;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MODEL_SEARCH_DEBOUNCE_MS = 250;

const ACCEPTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

const createPreviewImage = (file) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  file,
  previewUrl: URL.createObjectURL(file),
});

const buildEmptyItem = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  modelSearch: "",
  selectedProductId: "",
  modelMeta: null,
  modelOptions: [],
  loadingModels: false,
  showModelDropdown: false,
  isCompetitor: false,
  selectedCompetitorId: "",
  selectedProductGroupId: "",
  priceRangeMin: "",
  priceRangeMax: "",
  isPromotion: false,
  notes: "",
  images: [],
});

const buildItemFromExisting = (item) => ({
  id: `existing-${item.proof_item_id}`,
  modelSearch: item.product
    ? item.product.model_name || item.product.model_description || ""
    : "",
  selectedProductId: item.product?.product_id ? String(item.product.product_id) : "",
  modelMeta: item.product
    ? {
        modelDescription: item.product.model_description || "",
        colour: item.product.colour || "",
      }
    : null,
  modelOptions: [],
  loadingModels: false,
  showModelDropdown: false,
  isCompetitor: item.subject_type === "competitor",
  selectedCompetitorId: item.competitor?.competitor_id
    ? String(item.competitor.competitor_id)
    : "",
  selectedProductGroupId: item.product_group?.product_group_id
    ? String(item.product_group.product_group_id)
    : "",
  priceRangeMin: item.price_range_min != null ? String(item.price_range_min) : "",
  priceRangeMax: item.price_range_max != null ? String(item.price_range_max) : "",
  isPromotion: Boolean(item.is_promotion),
  notes: item.notes || "",
  images: Array.isArray(item.images)
    ? item.images.map((image) => ({
        id: `existing-${image.image_id}`,
        existingKey: image.image_s3_key,
        previewUrl: image.image_url,
        isExisting: true,
      }))
    : [],
});

const isBrokenModelCode = (variant) => {
  const modelCode = String(
    variant?.product_model_code || variant?.productModel?.product_model_code || "",
  ).trim();
  return !modelCode || modelCode === "000000";
};

const getVariantDisplayName = (variant) => {
  if (!variant) return "";
  const modelCode = variant.product_model_code?.trim();
  const fallbackModelCode = variant.productModel?.product_model_code?.trim();
  const description = variant.product_model_description?.trim();
  return modelCode || fallbackModelCode || description || "";
};

export default function DisplayProofForm({
  activeCheckIn,
  mode = "create",
  initialProof = null,
  proofId = null,
  onCancel,
  onSave,
}) {
  const isEditMode = mode === "edit";
  const { t } = useTranslation();
  const { currency } = useContext(CurrencyContext);
  const [proofType, setProofType] = useState(() => initialProof?.proof_type || "");
  const [proofTypes, setProofTypes] = useState([]);
  const [loadingProofTypes, setLoadingProofTypes] = useState(true);
  const [competitors, setCompetitors] = useState([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState(true);
  const [productGroups, setProductGroups] = useState([]);
  const [items, setItems] = useState(() =>
    isEditMode && Array.isArray(initialProof?.items) && initialProof.items.length
      ? initialProof.items.map(buildItemFromExisting)
      : [buildEmptyItem()],
  );
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const modelSearchTimeoutsRef = useRef({});
  const latestModelSearchRef = useRef({});

  const outletName = useMemo(() => {
    if (isEditMode) {
      return initialProof?.outlet_name || t("se.displayProof.unknownOutlet");
    }
    if (!activeCheckIn) return "-";
    return (
      activeCheckIn.outlet_name ||
      activeCheckIn.outlet?.bp_outlet_name ||
      t("se.displayProof.unknownOutlet")
    );
  }, [activeCheckIn, initialProof, isEditMode, t]);

  useEffect(() => {
    return () => {
      items.forEach((item) =>
        item.images.forEach((image) => URL.revokeObjectURL(image.previewUrl)),
      );
    };
  }, [items]);

  useEffect(() => {
    let isCancelled = false;

    const fetchProofTypes = async () => {
      try {
        setLoadingProofTypes(true);
        const response = await getDisplayProofTypes();
        if (isCancelled) return;
        const nextTypes = Array.isArray(response?.data) ? response.data : [];
        setProofTypes(nextTypes);
        setProofType((previous) => {
          if (previous && nextTypes.some((item) => item.code === previous)) {
            return previous;
          }
          return nextTypes[0]?.code || "";
        });
      } catch (requestError) {
        if (isCancelled) return;
        setProofTypes([]);
        setProofType("");
        setError(
          requestError?.response?.data?.error ||
            requestError?.response?.data?.message ||
            t("se.displayProof.errors.failedToLoadTypes"),
        );
      } finally {
        if (!isCancelled) {
          setLoadingProofTypes(false);
        }
      }
    };

    fetchProofTypes();
    return () => {
      isCancelled = true;
    };
  }, [t]);

  useEffect(() => {
    let isCancelled = false;

    const fetchCompetitors = async () => {
      try {
        setLoadingCompetitors(true);
        const response = await getDisplayProofCompetitors();
        if (isCancelled) return;
        setCompetitors(Array.isArray(response?.data) ? response.data : []);
      } catch (requestError) {
        if (!isCancelled) setCompetitors([]);
      } finally {
        if (!isCancelled) setLoadingCompetitors(false);
      }
    };

    fetchCompetitors();
    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const fetchProductGroups = async () => {
      try {
        const response = await getProductGroups();
        if (!isCancelled) setProductGroups(Array.isArray(response?.data) ? response.data : []);
      } catch (requestError) {
        if (!isCancelled) setProductGroups([]);
      }
    };

    fetchProductGroups();
    return () => {
      isCancelled = true;
    };
  }, []);

  const updateItem = useCallback((itemId, patch) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, ...(typeof patch === "function" ? patch(item) : patch) }
          : item,
      ),
    );
  }, []);

  const fetchModelOptionsForItem = useCallback(
    async (itemId, query = "") => {
      const normalizedQuery = String(query || "").trim();
      latestModelSearchRef.current[itemId] = normalizedQuery;
      updateItem(itemId, { loadingModels: true });

      try {
        const response = await api.get("/product-hierarchy/variants", {
          params: { q: normalizedQuery || undefined, limit: 30 },
        });

        if (
          response.data.success &&
          latestModelSearchRef.current[itemId] === normalizedQuery
        ) {
          updateItem(itemId, {
            modelOptions: (response.data.data || []).filter(
              (variant) => !isBrokenModelCode(variant),
            ),
          });
        }
      } catch (requestError) {
        if (latestModelSearchRef.current[itemId] === normalizedQuery) {
          updateItem(itemId, { modelOptions: [] });
        }
      } finally {
        if (latestModelSearchRef.current[itemId] === normalizedQuery) {
          updateItem(itemId, { loadingModels: false });
        }
      }
    },
    [updateItem],
  );

  const queueModelFetchForItem = useCallback(
    (itemId, query = "") => {
      if (modelSearchTimeoutsRef.current[itemId]) {
        clearTimeout(modelSearchTimeoutsRef.current[itemId]);
      }
      modelSearchTimeoutsRef.current[itemId] = setTimeout(() => {
        fetchModelOptionsForItem(itemId, query);
      }, MODEL_SEARCH_DEBOUNCE_MS);
    },
    [fetchModelOptionsForItem],
  );

  useEffect(() => {
    const timeouts = modelSearchTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".se-display-proof-model-dropdown")) {
        setItems((prev) =>
          prev.map((item) => ({ ...item, showModelDropdown: false })),
        );
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleModelSelect = (itemId, variant) => {
    updateItem(itemId, {
      selectedProductId: variant.product_id,
      modelSearch: getVariantDisplayName(variant),
      showModelDropdown: false,
      modelMeta: {
        modelDescription: variant.product_model_description || "",
        colour: variant.colour || "",
      },
    });
  };

  const handleModelInputChange = (itemId, value) => {
    updateItem(itemId, {
      modelSearch: value,
      selectedProductId: "",
      modelMeta: null,
      showModelDropdown: true,
    });
    queueModelFetchForItem(itemId, value);
  };

  const handleClearModel = (itemId) => {
    updateItem(itemId, {
      modelSearch: "",
      selectedProductId: "",
      modelMeta: null,
      showModelDropdown: false,
    });
  };

  const handleToggleCompetitor = (itemId, checked) => {
    setError("");
    if (checked) {

      updateItem(itemId, {
        isCompetitor: true,
        modelSearch: "",
        selectedProductId: "",
        modelMeta: null,
        showModelDropdown: false,
      });
    } else {
      updateItem(itemId, {
        isCompetitor: false,
        selectedCompetitorId: "",
        selectedProductGroupId: "",
        priceRangeMin: "",
        priceRangeMax: "",
      });
    }
  };

  const validateFiles = (selectedFiles) => {
    const validFiles = [];
    for (const file of selectedFiles) {
      if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
        setError(t("se.displayProof.errors.invalidType"));
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(t("se.displayProof.errors.fileTooLarge"));
        continue;
      }
      validFiles.push(file);
    }
    return validFiles;
  };

  const handleFileChange = (itemId, event) => {
    setError("");
    const fileList = Array.from(event.target.files || []);
    if (!fileList.length) return;

    setItems((prev) => {
      const target = prev.find((item) => item.id === itemId);
      if (!target) return prev;

      const remainingSlots = MAX_FILES_PER_ITEM - target.images.length;
      if (remainingSlots <= 0) {
        setError(t("se.displayProof.errors.maxFiles"));
        return prev;
      }

      const nextFiles = validateFiles(fileList.slice(0, remainingSlots));
      if (!nextFiles.length) return prev;

      const previewImages = nextFiles.map(createPreviewImage);
      return prev.map((item) =>
        item.id === itemId
          ? { ...item, images: [...item.images, ...previewImages] }
          : item,
      );
    });

    event.target.value = "";
  };

  const handleRemoveImage = (itemId, imageId) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const target = item.images.find((image) => image.id === imageId);
        if (target && !target.isExisting) {
          URL.revokeObjectURL(target.previewUrl);
        }
        return { ...item, images: item.images.filter((image) => image.id !== imageId) };
      }),
    );
  };

  const addItem = () => {
    if (items.length < MAX_ITEMS) {
      setItems((prev) => [...prev, buildEmptyItem()]);
    }
  };

  const removeItem = (itemId) => {
    if (items.length <= 1) return;

    const target = items.find((item) => item.id === itemId);
    target?.images.forEach((image) => URL.revokeObjectURL(image.previewUrl));

    if (modelSearchTimeoutsRef.current[itemId]) {
      clearTimeout(modelSearchTimeoutsRef.current[itemId]);
      delete modelSearchTimeoutsRef.current[itemId];
    }
    delete latestModelSearchRef.current[itemId];

    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!isEditMode && !activeCheckIn?.check_in_id) {
      setError(t("se.displayProof.errors.noActiveCheckIn"));
      return;
    }

    if (!proofType) {
      setError(t("se.displayProof.errors.proofTypeRequired"));
      return;
    }

    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      const itemNumber = index + 1;

      if (!item.images.length) {
        setError(t("se.displayProof.errors.itemPhotosRequired", { n: itemNumber }));
        return;
      }

      if (item.isCompetitor && !item.selectedCompetitorId) {
        setError(t("se.displayProof.errors.itemCompetitorRequired", { n: itemNumber }));
        return;
      }

      if (item.isCompetitor && !item.selectedProductGroupId) {
        setError(t("se.displayProof.errors.itemProductGroupRequired", { n: itemNumber }));
        return;
      }

      if (
        item.priceRangeMin !== "" &&
        item.priceRangeMax !== "" &&
        Number(item.priceRangeMin) > Number(item.priceRangeMax)
      ) {
        setError(t("se.displayProof.errors.itemPriceRangeInvalid", { n: itemNumber }));
        return;
      }

      if (proofType === "others" && !item.notes.trim()) {
        setError(
          t("se.displayProof.errors.itemNotesRequiredForOthers", { n: itemNumber }),
        );
        return;
      }
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      if (!isEditMode) {
        formData.append("check_in_id", String(activeCheckIn.check_in_id));
      }
      formData.append("proof_type", proofType);

      items.forEach((item, index) => {
        if (item.isCompetitor) {
          if (item.selectedCompetitorId) {
            formData.append(
              `items[${index}][competitor_id]`,
              String(item.selectedCompetitorId),
            );
          }
          if (item.selectedProductGroupId) {
            formData.append(
              `items[${index}][product_group_id]`,
              String(item.selectedProductGroupId),
            );
          }
        } else if (item.selectedProductId) {
          formData.append(`items[${index}][product_id]`, String(item.selectedProductId));
        }
        if (item.priceRangeMin !== "" || item.priceRangeMax !== "") {
          if (item.priceRangeMin !== "") {
            formData.append(`items[${index}][price_range_min]`, String(item.priceRangeMin));
          }
          if (item.priceRangeMax !== "") {
            formData.append(`items[${index}][price_range_max]`, String(item.priceRangeMax));
          }
          formData.append(`items[${index}][price_range_currency]`, currency);
        }
        if (item.isPromotion) {
          formData.append(`items[${index}][is_promotion]`, "true");
        }
        if (item.notes.trim()) {
          formData.append(`items[${index}][notes]`, item.notes.trim());
        }
        item.images
          .filter((image) => image.file)
          .forEach((image) => {
            formData.append(`items[${index}][photos]`, image.file);
          });

        if (isEditMode) {
          const keptExistingKeys = item.images
            .filter((image) => image.isExisting && image.existingKey)
            .map((image) => image.existingKey);
          formData.append(
            `items[${index}][existing_image_keys]`,
            JSON.stringify(keptExistingKeys),
          );
        }
      });

      const response = isEditMode
        ? await api.put(`/display-proofs/me/${proofId}`, formData)
        : await api.post("/display-proofs", formData);
      if (!response?.data?.success) {
        throw new Error(t("se.displayProof.errors.submitFailed"));
      }

      onSave?.(response.data.data);
    } catch (requestError) {
      const apiMessage =
        requestError?.response?.data?.error ||
        requestError?.response?.data?.message ||
        requestError?.message ||
        t(
          isEditMode
            ? "se.displayProof.errors.updateFailed"
            : "se.displayProof.errors.submitFailed",
        );
      setError(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="se-display-proof-container">
      <form className="se-display-proof-form" onSubmit={handleSubmit}>
        <p className="se-display-proof-subtitle">
          {t("se.displayProof.subtitle")}
        </p>

        <div className="se-sales-selected-visit">
          <div className="se-sales-selected-visit-top">
            <div>
              <div className="se-sales-selected-visit-label">
                {t("se.displayProof.outletLabel")}
              </div>
              <div className="se-sales-selected-visit-name">{outletName}</div>
            </div>
          </div>
          {activeCheckIn?.outlet_address && (
            <div className="se-sales-selected-visit-address">
              {activeCheckIn.outlet_address}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>
            {t("se.displayProof.proofTypeLabel")}
            <span className="required-asterisk" aria-hidden="true">*</span>
          </label>
          <select
            value={proofType}
            onChange={(event) => setProofType(event.target.value)}
            disabled={submitting || loadingProofTypes}
          >
            {loadingProofTypes ? (
              <option value="">{t("admin.common.loading")}</option>
            ) : proofTypes.length > 0 ? (
              proofTypes.map((type) => (
                <option key={type.proof_type_id || type.code} value={type.code}>
                  {type.name}
                </option>
              ))
            ) : (
              <option value="">{t("se.displayProof.errors.noTypesAvailable")}</option>
            )}
          </select>
        </div>

        {items.map((item, index) => (
          <div key={item.id} className="sale-item-grid-section">
            <div className="sale-item-section-header">
              <h4>{t("se.displayProof.itemTitle", { n: index + 1 })}</h4>
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="sale-item-remove-btn"
                  disabled={submitting}
                >
                  <Trash2 size={14} aria-hidden="true" />
                  {t("se.displayProof.removeItem")}
                </button>
              )}
            </div>

            {!item.isCompetitor && (
            <div className="form-group">
              <label>{t("se.displayProof.modelLabel")}</label>
              <div className="searchable-dropdown se-display-proof-model-dropdown">
                <div className="searchable-dropdown-input">
                  <Search size={18} className="searchable-dropdown-icon" />
                  <input
                    type="text"
                    value={item.modelSearch}
                    onChange={(event) =>
                      handleModelInputChange(item.id, event.target.value)
                    }
                    onFocus={() => {
                      updateItem(item.id, { showModelDropdown: true });
                      queueModelFetchForItem(item.id, item.modelSearch);
                    }}
                    placeholder={t("se.displayProof.searchModelQuick")}
                    className="searchable-dropdown-text-input"
                    disabled={submitting}
                  />
                  {item.modelSearch && (
                    <button
                      type="button"
                      className="se-display-proof-clear-model"
                      onClick={() => handleClearModel(item.id)}
                      aria-label={t("se.displayProof.clearModel")}
                      disabled={submitting}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {item.showModelDropdown && (
                  <div className="dropdown-list">
                    {item.loadingModels ? (
                      <div className="dropdown-item disabled">
                        {t("se.displayProof.loadingModels")}
                      </div>
                    ) : item.modelOptions.length > 0 ? (
                      item.modelOptions.slice(0, 30).map((variant) => (
                        <div
                          key={variant.product_id}
                          className="dropdown-item"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleModelSelect(item.id, variant);
                          }}
                        >
                          <div className="sale-model-option-name">
                            {getVariantDisplayName(variant)}
                          </div>
                          <div className="sale-model-option-meta">
                            <ColourMeta
                              label={`${t("se.displayProof.colourHelperLabel")} `}
                              colour={variant.colour}
                            />
                            {variant.colour && variant.product_model_description
                              ? " · "
                              : ""}
                            {variant.product_model_description || ""}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item disabled">
                        {t("se.displayProof.noModelsFound")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {item.modelMeta && (
                <div className="sale-model-meta">
                  <div>
                    {item.modelMeta.colour ? (
                      <ColourMeta
                        label={`${t("se.displayProof.colourHelperLabel")} `}
                        colour={item.modelMeta.colour}
                      />
                    ) : (
                      <>
                        <strong>{t("se.displayProof.colourHelperLabel")}</strong>{" "}
                        {t("se.displayProof.notAvailable")}
                      </>
                    )}
                  </div>
                  <div>
                    <strong>{t("se.displayProof.modelDescriptionHelperLabel")}</strong>{" "}
                    {item.modelMeta.modelDescription || t("se.displayProof.notAvailable")}
                  </div>
                </div>
              )}
            </div>
            )}

            {!item.selectedProductId && (
            <div className="form-group se-display-proof-competitor-group">
              <label className="se-display-proof-competitor-toggle">
                <input
                  type="checkbox"
                  checked={item.isCompetitor}
                  onChange={(event) =>
                    handleToggleCompetitor(item.id, event.target.checked)
                  }
                  disabled={submitting}
                />
                <span>{t("se.displayProof.competitorToggle")}</span>
              </label>
            </div>
            )}

            {item.isCompetitor && (
              <div className="form-group">
                <label>
                  {t("se.displayProof.competitorLabel")}
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <select
                  value={item.selectedCompetitorId}
                  onChange={(event) =>
                    updateItem(item.id, { selectedCompetitorId: event.target.value })
                  }
                  disabled={submitting || loadingCompetitors}
                >
                  <option value="">
                    {loadingCompetitors
                      ? t("admin.common.loading")
                      : t("se.displayProof.competitorPlaceholder")}
                  </option>
                  {competitors.map((competitor) => (
                    <option
                      key={competitor.competitor_id}
                      value={competitor.competitor_id}
                    >
                      {competitor.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {item.isCompetitor && (
              <div className="form-group">
                <label>
                  {t("se.displayProof.productGroupLabel")}
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <ProductGroupSelect
                  groups={productGroups}
                  value={item.selectedProductGroupId}
                  onChange={(productGroupId) =>
                    updateItem(item.id, { selectedProductGroupId: productGroupId })
                  }
                  placeholder={t("se.displayProof.productGroupPlaceholder")}
                  disabled={submitting}
                />
                <p className="se-display-proof-field-hint">
                  {t("se.displayProof.productGroupHint")}
                </p>
              </div>
            )}

            {(item.isCompetitor || item.selectedProductId) && (
              <div className="form-group se-display-proof-price-range-group">
                <label>{t("se.displayProof.priceRangeLabel")}</label>
                <PriceRangeInput
                  min={item.priceRangeMin}
                  max={item.priceRangeMax}
                  onMinChange={(value) => updateItem(item.id, { priceRangeMin: value })}
                  onMaxChange={(value) => updateItem(item.id, { priceRangeMax: value })}
                  currency={currency}
                  disabled={submitting}
                  minPlaceholder={t("se.displayProof.priceRangeMinPlaceholder")}
                  maxPlaceholder={t("se.displayProof.priceRangeMaxPlaceholder")}
                />
                <label className="se-display-proof-promotion-toggle">
                  <input
                    type="checkbox"
                    checked={item.isPromotion}
                    onChange={(event) =>
                      updateItem(item.id, { isPromotion: event.target.checked })
                    }
                    disabled={submitting}
                  />
                  <span>{t("se.displayProof.promotionToggle")}</span>
                </label>
              </div>
            )}

            <div className="form-group">
              <label>{t("se.displayProof.notesLabel")}</label>
              <textarea
                value={item.notes}
                onChange={(event) => updateItem(item.id, { notes: event.target.value })}
                placeholder={t("se.displayProof.notesPlaceholder")}
                rows={3}
                maxLength={3000}
                disabled={submitting}
              />
            </div>

            <div className="form-group">
              <label>
                {t("se.displayProof.photosLabel")}
                <span className="required-asterisk" aria-hidden="true">*</span>
              </label>
              <label className="se-display-proof-upload-trigger">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={(event) => handleFileChange(item.id, event)}
                  disabled={submitting || item.images.length >= MAX_FILES_PER_ITEM}
                  style={{ display: "none" }}
                />
                <div className="se-display-proof-upload-box">
                  <ImagePlus size={20} />
                  <span>{t("se.displayProof.addPhotos")}</span>
                  <small>
                    {t("se.displayProof.photoHint", { count: MAX_FILES_PER_ITEM })}
                  </small>
                </div>
              </label>

              {item.images.length > 0 && (
                <div className="se-display-proof-image-grid">
                  {item.images.map((image) => (
                    <div key={image.id} className="se-display-proof-image-item">
                      <img src={image.previewUrl} alt="Display proof preview" />
                      <button
                        type="button"
                        className="se-display-proof-remove-image"
                        onClick={() => handleRemoveImage(item.id, image.id)}
                        disabled={submitting}
                        aria-label={t("se.displayProof.removePhoto")}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {items.length < MAX_ITEMS && (
          <button
            type="button"
            onClick={addItem}
            className="sale-add-item-btn"
            disabled={submitting}
          >
            <span style={{ fontSize: "18px" }}>+</span>{" "}
            {t("se.displayProof.addItem")}
          </button>
        )}

        {error ? <div className="form-error">{error}</div> : null}

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            {t("se.displayProof.cancel")}
          </button>
          <button type="submit" className="btn-submit" disabled={submitting}>
            {isEditMode
              ? submitting
                ? t("se.displayProof.saving")
                : t("se.displayProof.saveChanges")
              : submitting
                ? t("se.displayProof.submitting")
                : t("se.displayProof.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
